import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { BeltRank } from "../../MembersArea";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface SongMooProps {
  selectedBelt: string;
  getBeltColor: (belt: string) => string;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

export default function SongMoo({
  selectedBelt,
  getBeltColor: _getBeltColor,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: SongMooProps) {
  if (!["BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;

  const body = (
    <>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
        <p className="mb-4 text-gray-700">
          SongMoo is the highest-level pattern, representing the pinnacle of martial arts mastery. Like the
          evergreen pine tree that remains strong and vibrant through all seasons, this pattern embodies
          enduring wisdom and unwavering spirit. This pattern is required for 5th Dan Black Belt and above.
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
        <p className="text-gray-700">
          SongMoo translates to &quot;Pine Tree Warrior&quot; or &quot;Evergreen Martial Spirit,&quot;
          symbolizing the eternal nature of true martial arts mastery. Like the pine tree that remains green
          through all seasons and weather conditions, the master maintains strength, flexibility, and wisdom
          through all of life&apos;s challenges.
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
        <p className="text-gray-700">
          The pattern consists of 72 movements that demonstrate the complete integration of all martial
          principles. The movements flow like branches of a pine tree - strong yet flexible, deeply rooted yet
          reaching skyward, combining the wisdom of age with the vitality of eternal growth.
        </p>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          <li>Integrated power expression</li>
          <li>Root-to-branch energy flow</li>
          <li>Seasonal adaptation principles</li>
          <li>Advanced combination techniques</li>
          <li>Wisdom manifestation methods</li>
          <li>Complete martial synthesis</li>
        </ul>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          <li>Eternal growth mindset</li>
          <li>Adaptability in technique</li>
          <li>Wisdom in application</li>
          <li>Balance of hard and soft</li>
          <li>Integration of all principles</li>
          <li>Teaching and legacy</li>
        </ul>
      </div>
    </>
  );

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="SongMoo">
            <TabsTrigger
              value="pattern-information"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Pattern Information
            </TabsTrigger>
            <TabsTrigger
              value="meaning"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Meaning
            </TabsTrigger>
            <TabsTrigger
              value="movements"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Movements
            </TabsTrigger>
            <TabsTrigger
              value="key-techniques"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Key Techniques
            </TabsTrigger>
            <TabsTrigger
              value="training-focus"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Training Focus
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="text-gray-700">
                SongMoo is the highest-level pattern, representing the pinnacle of martial arts mastery. Like
                the evergreen pine tree that remains strong and vibrant through all seasons, this pattern
                embodies enduring wisdom and unwavering spirit. This pattern is required for 5th Dan Black
                Belt and above.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="meaning" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
              <p className="text-gray-700">
                SongMoo translates to &quot;Pine Tree Warrior&quot; or &quot;Evergreen Martial Spirit,&quot;
                symbolizing the eternal nature of true martial arts mastery. Like the pine tree that remains
                green through all seasons and weather conditions, the master maintains strength, flexibility,
                and wisdom through all of life&apos;s challenges.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="movements" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
              <p className="text-gray-700">
                The pattern consists of 72 movements that demonstrate the complete integration of all martial
                principles. The movements flow like branches of a pine tree - strong yet flexible, deeply
                rooted yet reaching skyward, combining the wisdom of age with the vitality of eternal growth.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Integrated power expression</li>
                <li>Root-to-branch energy flow</li>
                <li>Seasonal adaptation principles</li>
                <li>Advanced combination techniques</li>
                <li>Wisdom manifestation methods</li>
                <li>Complete martial synthesis</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="training-focus" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Training Focus</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Eternal growth mindset</li>
                <li>Adaptability in technique</li>
                <li>Wisdom in application</li>
                <li>Balance of hard and soft</li>
                <li>Integration of all principles</li>
                <li>Teaching and legacy</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return <div className="mt-2 rounded-lg bg-gray-50 p-4">{body}</div>;
}
